/**
 * YES Game - Creative encouragement and positive affirmation
 * Ages 4-6, Building confidence and creativity
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CreatureType } from './types';
import { CREATURE_CARDS } from './data';

interface YesGameProps {
  language: 'en' | 'es';
  creature: CreatureType;
  onComplete: () => void;
}

const TEXT = {
  en: {
    title: 'The Magic Word Game!',
    question: 'Can you give your {creature} superpowers?',
    yes: 'YES!',
    celebrate: '✨ YES! You can do ANYTHING! ✨',
    encouragement: 'You are creative, brave, and full of wonderful ideas!',
    ready: 'Ready to choose superpowers?',
    letsGo: "Let's Go!",
    magicWords: ['YES!', 'I CAN!', 'LET\'S DO IT!', 'AMAZING!']
  },
  es: {
    title: '¡El Juego de la Palabra Mágica!',
    question: '¿Puedes darle superpoderes a tu {creature}?',
    yes: '¡SÍ!',
    celebrate: '✨ ¡SÍ! ¡Puedes hacer CUALQUIER COSA! ✨',
    encouragement: '¡Eres creativo, valiente y lleno de ideas maravillosas!',
    ready: '¿Listo para elegir superpoderes?',
    letsGo: '¡Vamos!',
    magicWords: ['¡SÍ!', '¡PUEDO!', '¡HAGÁMOSLO!', '¡INCREÍBLE!']
  }
};

export default function YesGame({ language, creature, onComplete }: YesGameProps) {
  const [phase, setPhase] = useState<'question' | 'celebration'>('question');
  const [selectedWord, setSelectedWord] = useState(0);

  const t = TEXT[language];
  const creatureData = CREATURE_CARDS.find(c => c.type === creature)!;

  const handleYes = (index: number) => {
    setSelectedWord(index);
    setPhase('celebration');

    // Auto-advance after celebration
    setTimeout(() => {
      onComplete();
    }, 3000);
  };

  return (
    <div className="min-h-[calc(100vh-80px)] flex flex-col items-center justify-center p-6">
      <div className="max-w-4xl w-full">
        <AnimatePresence mode="wait">
          {phase === 'question' && (
            <motion.div
              key="question"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="text-center"
            >
              {/* Title */}
              <motion.h2
                initial={{ scale: 0.92, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', stiffness: 200 }}
                className="text-5xl md:text-7xl mb-8"
                style={{
                  fontFamily: "'Fraunces', Georgia, serif",
                  fontWeight: 300,
                  color: '#2a6d5b',
                  letterSpacing: '-0.015em',
                }}
              >
                {t.title}
              </motion.h2>

              {/* Creature Display */}
              <motion.div
                animate={{
                  y: [0, -15, 0],
                  rotate: [0, 3, -3, 0]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }}
                className="text-9xl mb-8"
              >
                {creatureData.emoji}
              </motion.div>

              {/* Question */}
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="text-3xl md:text-5xl mb-12"
                style={{
                  fontFamily: "'Fraunces', Georgia, serif",
                  fontStyle: 'italic',
                  fontWeight: 500,
                  color: '#2a6d5b',
                }}
              >
                {t.question.replace('{creature}', creatureData.name[language])}
              </motion.p>

              {/* Magic Word Options */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl mx-auto">
                {t.magicWords.map((word, index) => (
                  <motion.button
                    key={index}
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{
                      delay: 0.5 + index * 0.1,
                      type: 'spring',
                      stiffness: 200
                    }}
                    whileHover={{
                      scale: 1.1,
                      rotate: [0, -5, 5, 0],
                      transition: { duration: 0.3 }
                    }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleYes(index)}
                    style={{
                      padding: '32px 24px',
                      background: '#42a57a',
                      color: '#ffffff',
                      fontFamily: "'Fraunces', Georgia, serif",
                      fontSize: 'clamp(36px, 5vw, 56px)',
                      fontWeight: 600,
                      borderRadius: 24,
                      border: 'none',
                      cursor: 'pointer',
                      boxShadow: '0 6px 22px rgba(66,165,122,0.28)',
                    }}
                  >
                    {word}
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}

          {phase === 'celebration' && (
            <motion.div
              key="celebration"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className="text-center"
            >
              {/* Celebration Emoji Burst */}
              <div className="relative mb-8">
                {[...Array(12)].map((_, i) => (
                  <motion.div
                    key={i}
                    initial={{ scale: 0, x: 0, y: 0, opacity: 1 }}
                    animate={{
                      scale: [0, 1.5, 1],
                      x: Math.cos((i * 30 * Math.PI) / 180) * 200,
                      y: Math.sin((i * 30 * Math.PI) / 180) * 200,
                      opacity: [1, 1, 0]
                    }}
                    transition={{
                      duration: 1.5,
                      ease: 'easeOut'
                    }}
                    className="absolute top-1/2 left-1/2 text-7xl"
                    style={{ transform: 'translate(-50%, -50%)' }}
                  >
                    {['⭐', '✨', '🌟', '💫'][i % 4]}
                  </motion.div>
                ))}

                <motion.div
                  animate={{
                    scale: [1, 1.2, 1],
                    rotate: [0, 360]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut'
                  }}
                  className="text-9xl"
                >
                  {creatureData.emoji}
                </motion.div>
              </div>

              {/* Celebration Text */}
              <motion.h2
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="text-5xl md:text-7xl mb-6"
                style={{
                  fontFamily: "'Fraunces', Georgia, serif",
                  fontWeight: 300,
                  color: '#2a6d5b',
                  letterSpacing: '-0.015em',
                }}
              >
                {t.celebrate}
              </motion.h2>

              <motion.p
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="text-2xl md:text-4xl mb-8"
                style={{
                  fontFamily: "'Fraunces', Georgia, serif",
                  fontStyle: 'italic',
                  fontWeight: 500,
                  color: '#2a6d5b',
                }}
              >
                {t.encouragement}
              </motion.p>

              <motion.p
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.7 }}
                style={{
                  fontFamily: "'Inter', system-ui, sans-serif",
                  fontSize: 22,
                  fontWeight: 500,
                  color: '#42a57a',
                }}
              >
                {t.ready}
              </motion.p>

              {/* Continue Button (optional - auto-advances) */}
              <motion.button
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 1 }}
                onClick={onComplete}
                style={{
                  marginTop: 32,
                  padding: '16px 36px',
                  background: '#42a57a',
                  color: '#ffffff',
                  fontFamily: "'Inter', system-ui, sans-serif",
                  fontSize: 22,
                  fontWeight: 600,
                  borderRadius: 999,
                  border: 'none',
                  cursor: 'pointer',
                  boxShadow: '0 4px 14px rgba(66,165,122,0.25)',
                }}
              >
                {t.letsGo} →
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
